import React from 'react';
import { Box, Typography } from '@mui/material';

// ---------------------------------------------------------------------------
// BrandMark — the single place the Cadence icon + wordmark is rendered.
//
// Centralising means the brand decision (Cadence in-app, Stratium on sign-in
// and in attribution only) cannot drift. Future changes land in one file.
//
// Performance: image asset is one of three pre-generated sizes in /public.
// Picking the nearest pre-sized asset keeps the decoded-image memory small
// and lets the browser cache the icon across surfaces.
// ---------------------------------------------------------------------------

export type BrandMarkSize = 'sm' | 'md' | 'lg';

interface BrandMarkProps {
  /** sm: sidebar (28px). md: header / signup (40px). lg: login hero (56px). */
  size?: BrandMarkSize;
  /** Show the "Cadence" wordmark next to the icon. Defaults to true. */
  showWordmark?: boolean;
  /** Show the "by Stratium" line below the wordmark. Use only on auth pages
   *  and the sidebar bottom attribution. Do not repeat elsewhere. */
  showStratiumLine?: boolean;
  /** Override label. Defaults to "Cadence". */
  label?: string;
  /** Accent colour for the wordmark gradient. Defaults to theme primary. */
  accent?: 'primary' | 'light';
}

const ICON_PX: Record<BrandMarkSize, number> = {
  sm: 28,
  md: 40,
  lg: 56,
};

// Pick the smallest pre-generated asset that still exceeds the display size,
// so the browser does not need to upsample. 64px covers sm; 192px covers md;
// logo192 is still fine for lg (no 1:1 match but it is the largest that fits
// most layouts without shipping 500KB).
const ICON_SRC: Record<BrandMarkSize, string> = {
  sm: '/favicon-64.png',
  md: '/logo192.png',
  lg: '/logo192.png',
};

const WORDMARK_PX: Record<BrandMarkSize, number> = {
  sm: 18,
  md: 22,
  lg: 28,
};

export function BrandMark({
  size = 'md',
  showWordmark = true,
  showStratiumLine = false,
  label = 'Cadence',
  accent = 'primary',
}: BrandMarkProps) {
  const iconPx = ICON_PX[size];
  const iconSrc = ICON_SRC[size];
  const wordmarkPx = WORDMARK_PX[size];

  return (
    <Box display="flex" alignItems="center" gap={1.25}>
      <Box
        component="img"
        src={iconSrc}
        alt=""
        aria-hidden
        width={iconPx}
        height={iconPx}
        sx={{
          display: 'block',
          borderRadius: 1.5,
          // Keep crisp edges on downscale.
          imageRendering: 'auto',
        }}
      />
      {showWordmark && (
        <Box>
          <Typography
            component="span"
            sx={{
              fontSize: wordmarkPx,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1,
              display: 'block',
              color: accent === 'light' ? '#F8FAFC' : 'primary.main',
            }}
          >
            {label}
          </Typography>
          {showStratiumLine && (
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                display: 'block',
                mt: 0.5,
              }}
            >
              by Stratium
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default BrandMark;
